/* eslint-disable no-console */
import { useState, useEffect, useCallback, useMemo } from "react";
import { getEmpleados, getRemuneracionesEmpleado } from "../../../utils";

// IMM Chile por año — actualizar anualmente cuando reajusten el salario mínimo.
const IMM_CHILE = {
  2024: 500000,
  2025: 510114,
  2026: 539000,
};
const getIMMChile = (año) => IMM_CHILE[año] || IMM_CHILE[2026];

/**
 * Hook que encapsula toda la lógica de RRHH: carga de empleados,
 * cálculo de sueldos prorrateados, beneficios (gratificación Chile, CTS/gratificación Perú),
 * egresos, y variantes por país y año anterior.
 *
 * @param {string} pais - País de sesión
 * @param {number} selectedYear - Año seleccionado
 * @param {number} monthsToShow - Meses a mostrar
 * @param {Array} empleadosRRHHChile - Empleados Chile (cargados en efecto externo)
 * @param {Array} empleadosRRHHPeru - Empleados Perú (cargados en efecto externo)
 */
export const useRRHHCalculations = (pais, selectedYear, monthsToShow, empleadosRRHHChile, empleadosRRHHPeru) => {
  const [empleadosRRHH, setEmpleadosRRHH] = useState([]);
  const [isEmpleadosRRHHLoading, setIsEmpleadosRRHHLoading] = useState(true);

  // ─── Cargar empleados del país de sesión ─────────────────────
  useEffect(() => {
    const fetchEmpleadosRRHH = async () => {
      if (!pais || !selectedYear) return;
      try {
        setIsEmpleadosRRHHLoading(true);
        const empleadosBase = await getEmpleados(pais);
        const empleadosConRem = await Promise.all(
          empleadosBase.map(async (emp) => {
            const rems = await getRemuneracionesEmpleado(emp.id);
            return { ...emp, remuneraciones: rems || [] };
          })
        );
        const ordenados = [...empleadosConRem].sort((a, b) => {
          const activoA = !a.fin_contrato || new Date(a.fin_contrato) > new Date();
          const activoB = !b.fin_contrato || new Date(b.fin_contrato) > new Date();
          if (activoA && !activoB) return -1;
          if (!activoA && activoB) return 1;
          const fechaA = new Date(a.inicio_contrato);
          const fechaB = new Date(b.inicio_contrato);
          if (fechaA.getTime() !== fechaB.getTime()) return fechaA - fechaB;
          return (parseFloat(b.sueldo_base) || 0) - (parseFloat(a.sueldo_base) || 0);
        });
        setEmpleadosRRHH(ordenados);
      } catch (err) {
        console.error("Error cargando datos RRHH:", err);
        setEmpleadosRRHH([]);
      } finally {
        setIsEmpleadosRRHHLoading(false);
      }
    };
    fetchEmpleadosRRHH();
  }, [pais, selectedYear]);

  // ─── Función core de cálculo de sueldo ───────────────────────
  // Núcleo compartido por las 4 variantes
  const calcSueldoCore = useCallback((empleado, monthIndex, año, paisCalc) => {
    if (!empleado) return 0;

    const primerDiaMes = new Date(año, monthIndex, 1);
    const ultimoDiaMes = new Date(año, monthIndex + 1, 0);
    const diasEnMes = ultimoDiaMes.getDate();

    const inicioContrato = new Date(empleado.inicio_contrato);
    const finContrato = empleado.fin_contrato ? new Date(empleado.fin_contrato) : null;

    if (inicioContrato > ultimoDiaMes || (finContrato && finContrato < primerDiaMes)) {
      return 0;
    }

    const vigentes = (empleado.remuneraciones || []).filter((rem) => {
      const inicioVig = new Date(rem.inicio_vigencia);
      const finVig = rem.fin_vigencia ? new Date(rem.fin_vigencia) : null;
      return inicioVig <= ultimoDiaMes && (!finVig || finVig >= primerDiaMes);
    });

    let sueldoBase = 0;
    if (vigentes.length > 0) {
      vigentes.sort((a, b) => new Date(b.inicio_vigencia) - new Date(a.inicio_vigencia));
      sueldoBase = Number(vigentes[0].sueldo_base) || 0;
    } else {
      sueldoBase = Number(empleado.sueldo_base) || 0;
    }

    if (sueldoBase === 0) return 0;

    let diasTrabajados = diasEnMes;

    if (inicioContrato >= primerDiaMes && inicioContrato <= ultimoDiaMes) {
      diasTrabajados = diasEnMes - inicioContrato.getDate() + 1;
    }

    if (finContrato && finContrato >= primerDiaMes && finContrato <= ultimoDiaMes) {
      const esUltimoDia =
        finContrato.getFullYear() === ultimoDiaMes.getFullYear() &&
        finContrato.getMonth() === ultimoDiaMes.getMonth() &&
        finContrato.getDate() === ultimoDiaMes.getDate();
      diasTrabajados = esUltimoDia ? diasEnMes : Math.min(diasTrabajados, finContrato.getDate());
    }

    const sueldoProrrateado = Math.round((sueldoBase * diasTrabajados) / diasEnMes);

    let gratProrrateada = 0;
    if (paisCalc === "Chile") {
      const IMM = getIMMChile(año);
      const topeAnual = 4.75 * IMM;
      const gratMensualMax = Math.round(topeAnual / 12);
      const gratCalculada = Math.round(sueldoBase * 0.25);
      const gratificacionMensual = Math.min(gratCalculada, gratMensualMax);
      gratProrrateada = Math.round((gratificacionMensual * diasTrabajados) / diasEnMes);
    }

    return sueldoProrrateado + gratProrrateada;
  }, []);

  // ─── 4 variantes de getSueldoBase ────────────────────────────

  const getSueldoBaseVigente = useCallback(
    (empleado, monthIndex) => calcSueldoCore(empleado, monthIndex, selectedYear, pais),
    [calcSueldoCore, selectedYear, pais]
  );

  const getSueldoBaseVigenteParaAno = useCallback(
    (empleado, monthIndex, año) => calcSueldoCore(empleado, monthIndex, año, pais),
    [calcSueldoCore, pais]
  );

  const getSueldoBaseVigenteChileFijo = useCallback(
    (empleado, monthIndex) => calcSueldoCore(empleado, monthIndex, selectedYear, "Chile"),
    [calcSueldoCore, selectedYear]
  );

  const getSueldoBaseVigentePeru = useCallback(
    (empleado, monthIndex) => calcSueldoCore(empleado, monthIndex, selectedYear, "Perú"),
    [calcSueldoCore, selectedYear]
  );

  // ─── Helper: calcular egresos para un set de empleados y año ─
  const calcEgresos = useCallback((emps, año) => {
    const mapa = Array(12).fill(0);
    emps.forEach((emp) => {
      if (emp.egreso_fecha && emp.egreso_monto) {
        const fechaEgreso = new Date(emp.egreso_fecha);
        if (fechaEgreso.getFullYear() === año) {
          const mesEgreso = fechaEgreso.getMonth();
          mapa[mesEgreso] += Number(emp.egreso_monto) || 0;
        }
      }
    });
    return mapa;
  }, []);

  // ─── Helper: agregar beneficios a base según país ────────────
  const agregarBeneficios = useCallback((base, emps, getSueldo, paisCalc, egresos, limitMonths = null) => {
    const conBeneficios = [...base];

    if (paisCalc === "Perú") {
      emps.forEach((emp) => {
        // Gratificación Junio (mes 5)
        if (limitMonths === null || 5 < limitMonths) {
          const sueldoJunio = getSueldo(emp, 5);
          if (sueldoJunio > 0) conBeneficios[5] += sueldoJunio;
        }
        // Gratificación Diciembre (mes 11)
        if (limitMonths === null || 11 < limitMonths) {
          const sueldoDiciembre = getSueldo(emp, 11);
          if (sueldoDiciembre > 0) conBeneficios[11] += sueldoDiciembre;
        }
        // CTS Mayo (mes 4)
        if (limitMonths === null || 4 < limitMonths) {
          const sueldoMayo = getSueldo(emp, 4);
          if (sueldoMayo > 0) conBeneficios[4] += sueldoMayo;
        }
        // CTS Noviembre (mes 10)
        if (limitMonths === null || 10 < limitMonths) {
          const sueldoNoviembre = getSueldo(emp, 10);
          if (sueldoNoviembre > 0) conBeneficios[10] += sueldoNoviembre;
        }
      });
    }

    // Chile: la gratificación ya está incluida en calcSueldoCore (sueldoProrrateado + gratProrrateada).
    // Acá NO se agrega nada extra para evitar doble conteo.

    for (let i = 0; i < 12; i++) {
      conBeneficios[i] += egresos[i];
    }

    return conBeneficios;
  }, []);

  // ─── Valores derivados: país de sesión ───────────────────────

  const rrhhMensual = useMemo(
    () =>
      Array(12)
        .fill(0)
        .map((_, i) => empleadosRRHH.reduce((sum, emp) => sum + getSueldoBaseVigente(emp, i), 0)),
    [empleadosRRHH, getSueldoBaseVigente]
  );

  const egresosMensual = useMemo(
    () => calcEgresos(empleadosRRHH, selectedYear),
    [empleadosRRHH, selectedYear, calcEgresos]
  );

  const rrhhMensualConBeneficios = useMemo(
    () => agregarBeneficios(rrhhMensual, empleadosRRHH, getSueldoBaseVigente, pais, egresosMensual),
    [rrhhMensual, empleadosRRHH, getSueldoBaseVigente, pais, egresosMensual, agregarBeneficios]
  );

  // ─── Valores derivados: año anterior ─────────────────────────

  const rrhhMensualAnoAnterior = useMemo(() => {
    const añoAnterior = selectedYear - 1;
    return Array(12)
      .fill(0)
      .map((_, i) => empleadosRRHH.reduce((sum, emp) => sum + getSueldoBaseVigenteParaAno(emp, i, añoAnterior), 0));
  }, [empleadosRRHH, getSueldoBaseVigenteParaAno, selectedYear]);

  const egresosMensualAnoAnterior = useMemo(
    () => calcEgresos(empleadosRRHH, selectedYear - 1),
    [empleadosRRHH, selectedYear, calcEgresos]
  );

  // eslint-disable-next-line no-unused-vars
  const rrhhMensualConBeneficiosAnoAnterior = useMemo(() => {
    const añoAnterior = selectedYear - 1;
    const getSueldoAA = (emp, i) => getSueldoBaseVigenteParaAno(emp, i, añoAnterior);
    return agregarBeneficios(rrhhMensualAnoAnterior, empleadosRRHH, getSueldoAA, pais, egresosMensualAnoAnterior);
  }, [
    rrhhMensualAnoAnterior,
    empleadosRRHH,
    getSueldoBaseVigenteParaAno,
    pais,
    egresosMensualAnoAnterior,
    selectedYear,
    agregarBeneficios,
  ]);

  // ─── Valores derivados: Chile fijo ───────────────────────────

  const egresosMensualChile = useMemo(
    () => calcEgresos(empleadosRRHHChile, selectedYear),
    [empleadosRRHHChile, selectedYear, calcEgresos]
  );

  const rrhhMensualChile = useMemo(() => {
    const base = Array(12)
      .fill(0)
      .map((_, i) => empleadosRRHHChile.reduce((sum, emp) => sum + getSueldoBaseVigenteChileFijo(emp, i), 0));
    return agregarBeneficios(base, empleadosRRHHChile, getSueldoBaseVigenteChileFijo, "Chile", egresosMensualChile);
  }, [empleadosRRHHChile, getSueldoBaseVigenteChileFijo, egresosMensualChile, agregarBeneficios]);

  // ─── Valores derivados: Perú fijo ───────────────────────────

  const egresosMensualPeru = useMemo(
    () => calcEgresos(empleadosRRHHPeru, selectedYear),
    [empleadosRRHHPeru, selectedYear, calcEgresos]
  );

  const rrhhMensualPeru = useMemo(() => {
    const base = Array(12)
      .fill(0)
      .map((_, i) => empleadosRRHHPeru.reduce((sum, emp) => sum + getSueldoBaseVigentePeru(emp, i), 0));
    return agregarBeneficios(
      base,
      empleadosRRHHPeru,
      getSueldoBaseVigentePeru,
      "Perú",
      egresosMensualPeru,
      monthsToShow
    );
  }, [empleadosRRHHPeru, getSueldoBaseVigentePeru, egresosMensualPeru, monthsToShow, agregarBeneficios]);

  return {
    empleadosRRHH,
    isEmpleadosRRHHLoading,
    getSueldoBaseVigente,
    rrhhMensualConBeneficios,
    rrhhMensualConBeneficiosAnoAnterior,
    rrhhMensualChile,
    rrhhMensualPeru,
  };
};
